import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
// import { UserHeader } from "../components/UserHeader";
import styles from "./css/ChildDetails.module.css";
import responsiveStyles from "./css/LessonSummariesPage.module.css";
import { LessonSummaryDto } from "../types/lesson-summary.types";
import { getLessonSummariesByChildId, createLessonSummary, updateLessonSummary, deleteLessonSummary } from "../services/lesson-summary.service";
import { showError, showConfirm, showSuccess } from "../utils/notifications";
import { safeLogger } from "../utils/logger";

// Hook לגילוי גודל מסך
const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowWidth;
};

const LessonSummariesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 768;
  const [childName, setChildName] = useState(location.state?.childName || "");
  // const [childCode, setChildCode] = useState<number | null>(null);
  const [summaries, setSummaries] = useState<LessonSummaryDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [showForm, setShowForm] = useState<boolean>(false);
  const [selectedSummary, setSelectedSummary] = useState<LessonSummaryDto | null>(null);
  const [expandedSummaryId, setExpandedSummaryId] = useState<number | null>(null);


  // מודל לסיכום חדש
  const [newSummary, setNewSummary] = useState<any>({
    Date: new Date().toISOString(),
    Title: '',
    LessonContent: '',
    ChildId: id ? parseInt(id) : 0,
    ExercisesPerformed: '',
    Conclusions: '',
    NextLessonPlans: '',
    AccessPermission: false,
    GeneralizationTasks: ''
  });


  // מודל לעריכת סיכום
  const [editingSummaryId, setEditingSummaryId] = useState<number | null>(null);
  const [editingSummary, setEditingSummary] = useState<LessonSummaryDto | null>(null);

  useEffect(() => {
    // טען סיכומים לפי id מהנתיב
    if (id) {
      loadSummaries(parseInt(id));
    } else {
      console.warn("[LessonSummariesPage] No id param in URL. Cannot load summaries.");
    }
  }, [childName, id]);

  const loadSummaries = async (childCodeParam: number) => {
    try {
      setLoading(true);
      const filtered = await getLessonSummariesByChildId(childCodeParam);
      // חובה: הדפסת הנתונים שמגיעים מה-API
      console.log("[LessonSummariesPage] summaries from API:", filtered);
      setSummaries(filtered);
      setError("");
    } catch (err) {
      // חובה: הדפסת שגיאות קריטיות
      console.error('[LessonSummariesPage] Error in loadSummaries:', err);
      setError("שגיאה בטעינת סיכומי השיעורים");
      safeLogger.error("Failed to load lesson summaries");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // אם זו תיבת סימון, נקבל את הערך שלה
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    if (editingSummaryId && editingSummary) {
      // מעדכנים את הסיכום שבעריכה
      setEditingSummary({ ...editingSummary, [name]: val });
    } else {
      // מעדכנים את הסיכום החדש
      setNewSummary({ ...newSummary, [name]: val });
    }
  };

const handleAddSummary = async () => {
  try {
    setLoading(true);
    setError("");

    // validation
    if (!newSummary.Date || !newSummary.Title || !newSummary.LessonContent) {
      setError("נא למלא את כל השדות החובה (תאריך, כותרת, תוכן)");
      return;
    }

    if (!newSummary.ChildId || newSummary.ChildId <= 0) {
      setError("מזהה ילד לא תקין");
      return;
    }

    const created = await createLessonSummary(newSummary);

    if (created && typeof created === "object") {
      // עדכון מקומי עם ה-ID האמיתי מהשרת
      setSummaries(prev => [...prev, created]);
    } else {
      // יצירת ID זמני עד טעינה מהשרת
      const tempSummary = { ...newSummary, ID: Date.now() };
      setSummaries(prev => [...prev, tempSummary]);
    }

    // טען מחדש את כל הסיכומים מהשרת כדי לוודא עקביות
    await loadSummaries(id ? parseInt(id) : 0);

    setShowForm(false);
    showSuccess("סיכום השיעור נוסף בהצלחה! 🎉");

    // איפוס הטופס
    setNewSummary({
      Date: new Date().toISOString().split("T")[0],
      Title: "",
      LessonContent: "",
      ChildId: id ? parseInt(id) : 0,
      ExercisesPerformed: "",
      Conclusions: "",
      NextLessonPlans: "",
      AccessPermission: false,
      GeneralizationTasks: ""
    });

  } catch (err: any) {
    const errorMessage = err?.response?.data?.message || err?.message || "שגיאה בהוספת סיכום שיעור";
    setError(errorMessage);
    safeLogger.error("Failed to create lesson summary");
    showError(`❌ ${errorMessage}`);
  } finally {
    setLoading(false);
  }
};

  const handleUpdateSummary = async () => {
    if (!editingSummaryId || !editingSummary) return;
    
    try {
      setLoading(true);
      setError("");
      
      // validation לפני השליחה
      if (!editingSummary.Date || !editingSummary.Title || !editingSummary.LessonContent) {
        setError("נא למלא את כל השדות החובה (תאריך, כותרת, תוכן)");
        setLoading(false);
        return;
      }
      
      await updateLessonSummary(editingSummaryId, editingSummary);
      // עדכון הרשימה המקומית
      setSummaries(summaries.map(s => 
        s.ID === editingSummaryId ? editingSummary : s
      ));
      setEditingSummaryId(null);
      setEditingSummary(null);
      showSuccess("סיכום השיעור עודכן בהצלחה! ✅");
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || "שגיאה בעדכון סיכום שיעור";
      setError(errorMessage);
      safeLogger.error("Failed to update lesson summary");
      showError(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSummary = async (id: number) => {
    if (!id || id === 0) {
      showError("לא ניתן למחוק סיכום ללא מזהה תקין");
      return;
    }
    
    if (!showConfirm("האם אתה בטוח שברצונך למחוק את הסיכום? ❗")) return;
    
    try {
      setLoading(true);
      setError("");
      await deleteLessonSummary(id);
      setSummaries(summaries.filter(s => s.ID !== id));
      showSuccess("סיכום השיעור נמחק בהצלחה! 🗑️");
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || "שגיאה במחיקת סיכום שיעור";
      setError(errorMessage);
      safeLogger.error("Failed to delete lesson summary");
      showError(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (summary: LessonSummaryDto) => {
    if (!summary.ID) {
      showError("לא ניתן לערוך סיכום ללא מזהה תקין");
      return;
    }
    setSelectedSummary(summary);
    setEditingSummaryId(summary.ID || null);
    setEditingSummary({ ...summary });
  };

  const cancelEditing = () => {
    setEditingSummaryId(null);
    setEditingSummary(null);
  };

  const toggleExpanded = (summaryId: number) => {
    if (!summaryId || summaryId === 0) {
      return;
    }
    setExpandedSummaryId(expandedSummaryId === summaryId ? null : summaryId);
  };
  
  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return 'תאריך לא זמין';
      
      // אם התאריך כבר בפורמט עברי
      if (dateString.includes('/')) {
        return dateString;
      }
      
      // המרה מ-ISO string לתאריך עברי
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // אם לא ניתן להמיר, החזר כמו שהוא
      }
      
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (e) {
      console.error('Error formatting date:', dateString, e);
      return dateString || 'תאריך לא זמין';
    }
  };

  return (
    <>
      {/* DebugChildCode component removed */}
      {/* <UserHeader /> */}
      <div 
        className={styles.pageContainer}
        style={{
          background: 'transparent',
          minHeight: '100vh',
          padding: '30px 0'
        }}
      >
        <div className={styles.betaBanner}>
            <p>
              לכל מטופל באפשרותך להגדיר אלו סיכומי שיעור יוצגו להורים.
            </p>
        </div>
        
        <div className={styles.container} style={{
          maxWidth: '95%', // הרחבה משמעותית - כמעט כל הרוחב
          width: '95%',
          margin: '0 auto',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
          padding: !isMobile ? '35px 40px' : '25px 20px',
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '15px'
          }}>
            <div></div> {/* Placeholder for flex alignment */}
          </div>

          <h2 style={{
            fontSize: !isMobile ? '2.2rem' : '1.8rem',
            marginBottom: '30px',
            color: '#471f1f',
            textAlign: 'center',
            fontWeight: 600,
            position: 'relative',
            paddingBottom: '15px'
          }}>
            סיכומי שיעור - {childName}
            <span style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '3px',
              background: 'linear-gradient(to right, #a2b087, #ba9484)',
              borderRadius: '2px',
              display: 'block'
            }}></span>
          </h2>
          
          {error && (
            <div style={{ 
              backgroundColor: '#fff3f3', 
              color: '#d32f2f', 
              padding: '12px 20px', 
              borderRadius: '8px',
              margin: '15px 0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}



          {/* טופס הוספת סיכום חדש */}
          {showForm && (
            <div style={{ 
              backgroundColor: 'rgba(255,255,255,0.95)', 
              padding: !isMobile ? '30px' : '20px', 
              borderRadius: '15px',
              boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              marginBottom: '30px',
              border: '1px solid #eaeaea'
            }}>
              <h3 style={{ 
                textAlign: 'center', 
                color: '#471f1f',
                marginBottom: '25px',
                fontSize: !isMobile ? '1.6rem' : '1.4rem',
                fontWeight: '600',
                position: 'relative',
                paddingBottom: '12px',
                display: 'inline-block',
                margin: '0 auto 25px'
              }}>
                הוספת סיכום שיעור חדש
                <span style={{
                  position: 'absolute',
                  bottom: 0,
                  left: '10%',
                  right: '10%',
                  height: '3px',
                  background: 'linear-gradient(to right, #a2b087, #ba9484)',
                  borderRadius: '2px',
                  display: 'block'
                }}></span>
              </h3>

              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                padding: '10px',
                backgroundColor: '#fff3e0',
                borderRadius: '8px',
                fontSize: '0.9rem',
                color: '#f57c00'
              }}>
                <span style={{ color: '#ff6b6b' }}>*</span> שדות חובה
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: !isMobile ? '1fr 1fr' : '1fr', 
                gap: '15px', 
                marginBottom: '15px' 
              }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '5px', 
                    fontWeight: 'bold',
                    color: '#471f1f' 
                  }}>
                    תאריך: <span style={{ color: '#ff6b6b' }}>*</span>
                  </label>
                  <input
                    type="date"
                    name="Date"
                    value={newSummary.Date}
                    onChange={handleInputChange}
                    style={{ 
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: !newSummary.Date ? '2px solid #ff6b6b' : '1px solid #ddd',
                      fontSize: '1rem',
                      transition: 'border 0.2s ease'
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '5px', 
                    fontWeight: 'bold',
                    color: '#471f1f' 
                  }}>
                    כותרת: <span style={{ color: '#ff6b6b' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="Title"
                    value={newSummary.Title}
                    onChange={handleInputChange}
                    style={{ 
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: !newSummary.Title ? '2px solid #ff6b6b' : '1px solid #ddd',
                      fontSize: '1rem',
                      transition: 'border 0.2s ease'
                    }}
                    required
                    placeholder="נושא השיעור"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  fontWeight: 'bold',
                  color: '#471f1f' 
                }}>
                  תוכן השיעור: <span style={{ color: '#ff6b6b' }}>*</span>
                </label>
                <textarea
                  name="LessonContent"
                  value={newSummary.LessonContent}
                  onChange={handleInputChange}
                  style={{ 
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: !newSummary.LessonContent ? '2px solid #ff6b6b' : '1px solid #ddd',
                    fontSize: '1rem',
                    minHeight: '120px',
                    maxHeight: '200px', // הגבלת גובה מקסימלי
                    overflowY: 'auto', // גלילה אוטומטית
                    resize: 'vertical', // אפשרות לשנות גובה
                    transition: 'border 0.2s ease'
                  }}
                  required
                  placeholder="תיאור מפורט של מהלך השיעור"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  fontWeight: 'bold',
                  color: '#471f1f' 
                }}>
                  תרגילים שבוצעו:
                </label>
                <textarea
                  name="ExercisesPerformed"
                  value={newSummary.ExercisesPerformed || ''}
                  onChange={handleInputChange}
                  style={{ 
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    minHeight: '80px',
                    maxHeight: '150px', // הגבלת גובה מקסימלי
                    overflowY: 'auto', // גלילה אוטומטית
                    resize: 'vertical' // אפשרות לשנות גובה
                  }}
                  placeholder="תרגילים ופעילויות שבוצעו במהלך השיעור"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  fontWeight: 'bold',
                  color: '#471f1f' 
                }}>
                  מסקנות:
                </label>
                <textarea
                  name="Conclusions"
                  value={newSummary.Conclusions || ''}
                  onChange={handleInputChange}
                  style={{ 
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    minHeight: '80px',
                    maxHeight: '150px', // הגבלת גובה מקסימלי
                    overflowY: 'auto', // גלילה אוטומטית
                    resize: 'vertical' // אפשרות לשנות גובה
                  }}
                  placeholder="מסקנות והתקדמות הילד"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  fontWeight: 'bold',
                  color: '#471f1f' 
                }}>
                  תוכניות לשיעור הבא:
                </label>
                <textarea
                  name="NextLessonPlans"
                  value={newSummary.NextLessonPlans || ''}
                  onChange={handleInputChange}
                  style={{ 
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    minHeight: '80px',
                    maxHeight: '150px', // הגבלת גובה מקסימלי
                    overflowY: 'auto', // גלילה אוטומטית
                    resize: 'vertical' // אפשרות לשנות גובה
                  }}
                  placeholder="מה מתוכנן לשיעור הבא"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  fontWeight: 'bold',
                  color: '#471f1f' 
                }}>
                  משימות הכללה:
                </label>
                <textarea
                  name="GeneralizationTasks"
                  value={newSummary.GeneralizationTasks || ''}
                  onChange={handleInputChange}
                  style={{ 
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    minHeight: '80px',
                    maxHeight: '150px', // הגבלת גובה מקסימלי
                    overflowY: 'auto', // גלילה אוטומטית
                    resize: 'vertical' // אפשרות לשנות גובה
                  }}
                  placeholder="משימות להכללת הכישורים שנלמדו"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 'bold',
                  color: '#471f1f',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="AccessPermission"
                    checked={newSummary.AccessPermission}
                    onChange={handleInputChange}
                    style={{ marginLeft: '8px', transform: 'scale(1.2)' }}
                  />
                  הצג להורים
                </label>
              </div>

              <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <button
                  onClick={handleAddSummary}
                  style={{
                    backgroundColor: (!newSummary.Date || !newSummary.Title || !newSummary.LessonContent) 
                      ? '#cccccc' 
                      : '#a2b087',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 35px',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    cursor: (!newSummary.Date || !newSummary.Title || !newSummary.LessonContent) 
                      ? 'not-allowed' 
                      : 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    minWidth: '200px',
                    opacity: (!newSummary.Date || !newSummary.Title || !newSummary.LessonContent) 
                      ? 0.6 
                      : 1
                  }}
                  disabled={loading || !newSummary.Date || !newSummary.Title || !newSummary.LessonContent}
                  onMouseOver={(e) => {
                    if (!(!newSummary.Date || !newSummary.Title || !newSummary.LessonContent)) {
                      e.currentTarget.style.backgroundColor = '#91a074';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!(!newSummary.Date || !newSummary.Title || !newSummary.LessonContent)) {
                      e.currentTarget.style.backgroundColor = '#a2b087';
                    }
                  }}
                >
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                      <div className="spinner" style={{
                        width: '18px',
                        height: '18px',
                        border: '3px solid rgba(255,255,255,0.3)',
                        borderRadius: '50%',
                        borderTop: '3px solid white',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      שומר...
                    </span>
                  ) : (
                    <span>שמור סיכום שיעור</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* רשימת סיכומי שיעור עם תצוגה מקופלת */}
          {loading && !showForm ? (
            <div style={{ textAlign: 'center', padding: '30px', fontSize: '1.2rem', color: '#666' }}>
              <div className="loader" style={{
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #a2b087',
                borderRadius: '50%',
                marginBottom: '15px',
                animation: 'spin 1s linear infinite',
              }}></div>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
              <p>טוען סיכומי שיעור...</p>
            </div>
          ) : summaries.length > 0 ? (
            <div>
              {/* כותרות הטבלה */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: !isMobile 
                  ? '100px 250px 1fr 200px 250px' // הרחבה משמעותית של כל העמודות
                  : '40px 1fr 100px',
                backgroundColor: '#f8f9fa',
                padding: !isMobile ? '15px 20px' : '12px 10px',
                borderRadius: '15px 15px 0 0',
                borderBottom: '2px solid #e0e0e0',
                fontWeight: 'bold',
                fontSize: !isMobile ? '1.2rem' : '0.9rem',
                color: '#471f1f',
                marginBottom: '0'
              }}>
                <div style={{ textAlign: 'center' }}>פרטים</div>
                {!isMobile ? (
                  <>
                    <div style={{ textAlign: 'center' }}>תאריך</div>
                    <div style={{ textAlign: 'center' }}>כותרת</div>
                    <div style={{ textAlign: 'center' }}>סטטוס</div>
                    <div style={{ textAlign: 'center' }}>פעולות</div>
                  </>
                ) : (
                  <>
                    <div style={{ textAlign: 'center' }}>סיכום שיעור</div>
                    <div style={{ textAlign: 'center' }}>פעולות</div>
                  </>
                )}
              </div>
              
              <div style={{
                marginBottom: '30px',
                borderRadius: '0 0 15px 15px',
                overflow: 'hidden',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                backgroundColor: 'white',
                maxHeight: summaries.length > 7 ? '600px' : 'none', // גלילה אחרי 7 סיכומים
                overflowY: summaries.length > 7 ? 'auto' : 'visible'
              }}>
                {summaries
                  .sort((a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime())
                  .map((summary) => (
                    <div 
                      key={`summary-${summary.ID}`}
                      style={{
                        borderBottom: '1px solid #eee',
                        backgroundColor: summary.AccessPermission ? 'rgba(232, 245, 233, 0.1)' : 'white',
                      }}
                    >
                      {/* שורת הטבלה הראשית */}
                      <div 
                        style={{
                          display: 'grid',
                          gridTemplateColumns: !isMobile 
                            ? '100px 250px 1fr 200px 250px' // הרחבה משמעותית של כל העמודות
                            : '40px 1fr 100px',
                          alignItems: 'center',
                          padding: !isMobile ? '20px' : '15px 10px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderLeft: summary.AccessPermission ? '4px solid #4caf50' : '4px solid transparent',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = summary.AccessPermission ? 'rgba(232, 245, 233, 0.1)' : 'white';
                        }}
                      >
                        {/* כפתור הרחבה עם חץ גדול יותר ואזור לחיצה מורחב */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            if (summary.ID) toggleExpanded(summary.ID);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: '10px', // אזור לחיצה מורחב
                            borderRadius: '50%',
                            transition: 'all 0.3s ease'
                          }}
                          title={expandedSummaryId === summary.ID ? 'סגור פרטים' : 'הצג פרטים'}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(162, 176, 135, 0.1)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span
                            style={{
                              fontSize: !isMobile ? '2rem' : '1.5rem', // הגדלת החץ משמעותית
                              fontWeight: 'bold',
                              transform: expandedSummaryId === summary.ID ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s ease',
                              color: '#a2b087',
                              lineHeight: '1'
                            }}
                          >
                            ▼
                          </span>
                        </div>

                        {/* תאריך וכותרת - במובייל בעמודה אחת */}
                        {!isMobile ? (
                          <>
                            {/* תאריך - עם אפשרות לחיצה */}
                            <div 
                              style={{ 
                                fontSize: '1.1rem', 
                                fontWeight: '600',
                                color: '#471f1f',
                                textAlign: 'center',
                                padding: '10px', // אזור לחיצה מורחב
                                cursor: 'pointer',
                                borderRadius: '8px',
                                transition: 'background-color 0.2s ease'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (summary.ID) toggleExpanded(summary.ID);
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(162, 176, 135, 0.05)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {summary.Date ? formatDate(summary.Date) : 'תאריך לא זמין'}
                            </div>

                            {/* כותרת - עם אפשרות לחיצה */}
                            <div 
                              style={{ 
                                fontSize: '1.15rem', 
                                fontWeight: 'bold',
                                color: '#333',
                                paddingRight: '10px',
                                cursor: 'pointer',
                                textAlign: 'right',
                                padding: '10px', // אזור לחיצה מורחב
                                borderRadius: '8px',
                                transition: 'background-color 0.2s ease'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (summary.ID) toggleExpanded(summary.ID);
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(162, 176, 135, 0.05)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {summary.Title || 'ללא כותרת'}
                            </div>

                            {/* סטטוס */}
                            <div style={{ textAlign: 'center' }}>
                              {summary.AccessPermission ? (
                                <span style={{ 
                                  display: 'inline-block',
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32',
                                  padding: '6px 12px',
                                  borderRadius: '20px',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                }}>
                                  ✓ מוצג להורים
                                </span>
                              ) : (
                                <span style={{ 
                                  display: 'inline-block',
                                  backgroundColor: '#f5f5f5',
                                  color: '#666',
                                  padding: '6px 12px',
                                  borderRadius: '20px',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                }}>
                                  פרטי
                                </span>
                              )}
                            </div>
                          </>
                        ) : (
                          /* במובייל - תאריך וכותרת יחד */
                          <div 
                            onClick={() => summary.ID && toggleExpanded(summary.ID)}
                            style={{ 
                              paddingRight: '10px'
                            }}
                          >
                            <div style={{ 
                              fontSize: '1.1rem', 
                              fontWeight: 'bold',
                              color: '#333',
                              marginBottom: '4px'
                            }}>
                              {summary.Title || 'ללא כותרת'}
                            </div>
                            <div style={{ 
                              fontSize: '0.95rem',
                              color: '#666',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <span>{summary.Date ? formatDate(summary.Date) : 'תאריך לא זמין'}</span>
                              {summary.AccessPermission && (
                                <span style={{ 
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32',
                                  padding: '2px 6px',
                                  borderRadius: '10px',
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                }}>
                                  ✓
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* כפתורי פעולה */}
                        <div style={{ 
                          display: 'flex', 
                          gap: !isMobile ? '10px' : '8px', 
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          {summary.ID && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditing(summary);
                              }}
                              style={{
                                backgroundColor: '#d4ecee', // צבע כחול בהיר מהפלטה
                                border: 'none',
                                borderRadius: '50%',
                                width: !isMobile ? '40px' : '35px',
                                height: !isMobile ? '40px' : '35px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                fontSize: !isMobile ? '1.1rem' : '1rem'
                              }}
                              title="ערוך סיכום"
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#b3e0e5'} // כהה יותר בהובר
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#d4ecee'}
                            >
                              ✏️
                            </button>
                          )}
                          {summary.ID && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (summary.ID) handleDeleteSummary(summary.ID);
                              }}
                              style={{
                                backgroundColor: '#eec0c6', // צבע ורוד מהפלטה
                                border: 'none',
                                borderRadius: '50%',
                                width: !isMobile ? '40px' : '35px',
                                height: !isMobile ? '40px' : '35px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                fontSize: !isMobile ? '1.1rem' : '1rem'
                              }}
                              title="מחק סיכום"
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#d8a8b0'} // כהה יותר בהובר
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#eec0c6'}
                            >
                              🗑️
                            </button>
                          )}
                          {!summary.ID && (
                            <div style={{
                              fontSize: '0.9rem',
                              color: '#999',
                              fontStyle: 'italic',
                              textAlign: 'center'
                            }}>
                              נטען...
                            </div>
                          )}
                        </div>
                      </div>

                      {/* תוכן מורחב */}
                      {expandedSummaryId === summary.ID && (
                        <div 
                          style={{
                            backgroundColor: '#fafafa',
                            padding: !isMobile ? '25px 30px' : '20px 15px',
                            borderTop: '1px solid #eee',
                            animation: 'slideDown 0.3s ease-out'
                          }}
                        >
                          <style>{`
                            @keyframes slideDown {
                              from {
                                opacity: 0;
                                transform: translateY(-10px);
                              }
                              to {
                                opacity: 1;
                                transform: translateY(0);
                              }
                            }
                          `}</style>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: !isMobile ? '20px' : '15px'
                          }}>
                            {/* תוכן השיעור */}
                            <div style={{
                              backgroundColor: 'white',
                              padding: !isMobile ? '20px' : '15px',
                              borderRadius: '12px',
                              border: '1px solid #e0e0e0',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                            }}>
                              <h4 style={{
                                color: '#471f1f',
                                fontSize: !isMobile ? '1.2rem' : '1.1rem',
                                marginTop: '0',
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                📝 תוכן השיעור:
                              </h4>
                              <div style={{
                                whiteSpace: 'pre-wrap',
                                lineHeight: '1.6',
                                fontSize: !isMobile ? '1.05rem' : '1rem',
                                color: '#444',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {summary.LessonContent || 'אין תוכן זמין'}
                              </div>
                            </div>
                            
                            {/* שאר השדות בגריד */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: !isMobile 
                                ? 'repeat(auto-fit, minmax(300px, 1fr))' 
                                : '1fr',
                              gap: !isMobile ? '20px' : '15px'
                            }}>
                              {summary.ExercisesPerformed && (
                                <div style={{
                                  backgroundColor: 'white',
                                  padding: !isMobile ? '20px' : '15px',
                                  borderRadius: '12px',
                                  border: '1px solid #e0e0e0',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                }}>
                                  <h4 style={{
                                    color: '#471f1f',
                                    fontSize: !isMobile ? '1.1rem' : '1rem',
                                    marginTop: '0',
                                    marginBottom: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}>
                                    🏃‍♀️ תרגילים שבוצעו:
                                  </h4>
                                  <div style={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '1.5',
                                    fontSize: !isMobile ? '1rem' : '0.95rem',
                                    color: '#555',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    maxHeight: '150px', // הגבלת גובה מקסימלי
                                    overflowY: 'auto', // גלילה אוטומטית
                                    padding: '5px 0'
                                  }}>
                                    {summary.ExercisesPerformed}
                                  </div>
                                </div>
                              )}
                              
                              {summary.Conclusions && (
                                <div style={{
                                  backgroundColor: 'white',
                                  padding: !isMobile ? '20px' : '15px',
                                  borderRadius: '12px',
                                  border: '1px solid #e0e0e0',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                }}>
                                  <h4 style={{
                                    color: '#471f1f',
                                    fontSize: !isMobile ? '1.1rem' : '1rem',
                                    marginTop: '0',
                                    marginBottom: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}>
                                    💡 מסקנות:
                                  </h4>
                                  <div style={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '1.5',
                                    fontSize: !isMobile ? '1rem' : '0.95rem',
                                    color: '#555',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    maxHeight: '150px', // הגבלת גובה מקסימלי
                                    overflowY: 'auto', // גלילה אוטומטית
                                    padding: '5px 0'
                                  }}>
                                    {summary.Conclusions}
                                  </div>
                                </div>
                              )}
                              
                              {summary.NextLessonPlans && (
                                <div style={{
                                  backgroundColor: 'white',
                                  padding: !isMobile ? '20px' : '15px',
                                  borderRadius: '12px',
                                  border: '1px solid #e0e0e0',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                }}>
                                  <h4 style={{
                                    color: '#471f1f',
                                    fontSize: !isMobile ? '1.1rem' : '1rem',
                                    marginTop: '0',
                                    marginBottom: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}>
                                    📅 תוכניות לשיעור הבא:
                                  </h4>
                                  <div style={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '1.5',
                                    fontSize: !isMobile ? '1rem' : '0.95rem',
                                    color: '#555',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    maxHeight: '150px', // הגבלת גובה מקסימלי
                                    overflowY: 'auto', // גלילה אוטומטית
                                    padding: '5px 0'
                                  }}>
                                    {summary.NextLessonPlans}
                                  </div>
                                </div>
                              )}
                              
                              {summary.GeneralizationTasks && (
                                <div style={{
                                  backgroundColor: 'white',
                                  padding: !isMobile ? '20px' : '15px',
                                  borderRadius: '12px',
                                  border: '1px solid #e0e0e0',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                }}>
                                  <h4 style={{
                                    color: '#471f1f',
                                    fontSize: !isMobile ? '1.1rem' : '1rem',
                                    marginTop: '0',
                                    marginBottom: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}>
                                    🔄 משימות הכללה:
                                  </h4>
                                  <div style={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '1.5',
                                    fontSize: !isMobile ? '1rem' : '0.95rem',
                                    color: '#555',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    maxHeight: '150px', // הגבלת גובה מקסימלי
                                    overflowY: 'auto', // גלילה אוטומטית
                                    padding: '5px 0'
                                  }}>
                                    {summary.GeneralizationTasks}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>

              {/* מצב עריכת סיכום קיים */}
              {editingSummaryId !== null && editingSummary && (
                <div 
                  key={`editing-${editingSummaryId}`} 
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    padding: !isMobile ? '25px' : '20px',
                    borderRadius: '12px',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
                    marginBottom: '30px',
                    position: 'relative',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <h3 style={{ 
                    textAlign: 'center', 
                    color: '#471f1f',
                    marginBottom: '25px',
                    fontSize: !isMobile ? '1.6rem' : '1.4rem',
                    fontWeight: '600',
                    position: 'relative',
                    paddingBottom: '12px',
                    display: 'inline-block',
                    margin: '0 auto 25px'
                  }}>
                    עריכת סיכום שיעור
                    <span style={{
                      position: 'absolute',
                      bottom: 0,
                      left: '10%',
                      right: '10%',
                      height: '3px',
                      background: 'linear-gradient(to right, #a2b087, #ba9484)',
                      borderRadius: '2px',
                      display: 'block'
                    }}></span>
                  </h3>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: !isMobile ? '1fr 1fr' : '1fr', 
                    gap: '15px', 
                    marginBottom: '15px' 
                  }}>
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '5px', 
                        fontWeight: 'bold',
                        color: '#471f1f' 
                      }}>
                        תאריך:
                      </label>
                      <input
                        type="date"
                        name="Date"
                        value={editingSummary.Date}
                        onChange={handleInputChange}
                        style={{ 
                          width: '100%',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '1rem'
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '5px', 
                        fontWeight: 'bold',
                        color: '#471f1f' 
                      }}>
                        כותרת:
                      </label>
                      <input
                        type="text"
                        name="Title"
                        value={editingSummary.Title}
                        onChange={handleInputChange}
                        style={{ 
                          width: '100%',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '1rem'
                        }}
                        required
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      color: '#471f1f' 
                    }}>
                      תוכן השיעור:
                    </label>
                    <textarea
                      name="LessonContent"
                      value={editingSummary.LessonContent}
                      onChange={handleInputChange}
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        fontSize: '1rem',
                        minHeight: '120px'
                      }}
                      required
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      color: '#471f1f' 
                    }}>
                      תרגילים שבוצעו:
                    </label>
                    <textarea
                      name="ExercisesPerformed"
                      value={editingSummary.ExercisesPerformed || ''}
                      onChange={handleInputChange}
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        fontSize: '1rem',
                        minHeight: '80px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      color: '#471f1f' 
                    }}>
                      מסקנות:
                    </label>
                    <textarea
                      name="Conclusions"
                      value={editingSummary.Conclusions || ''}
                      onChange={handleInputChange}
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        fontSize: '1rem',
                        minHeight: '80px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      color: '#471f1f' 
                    }}>
                      תוכניות לשיעור הבא:
                    </label>
                    <textarea
                      name="NextLessonPlans"
                      value={editingSummary.NextLessonPlans || ''}
                      onChange={handleInputChange}
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        fontSize: '1rem',
                        minHeight: '80px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      color: '#471f1f' 
                    }}>
                      משימות הכללה:
                    </label>
                    <textarea
                      name="GeneralizationTasks"
                      value={editingSummary.GeneralizationTasks || ''}
                      onChange={handleInputChange}
                      style={{ 
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        fontSize: '1rem',
                        minHeight: '80px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#471f1f',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        name="AccessPermission"
                        checked={editingSummary.AccessPermission}
                        onChange={handleInputChange}
                        style={{ marginLeft: '8px', transform: 'scale(1.2)' }}
                      />
                      הצג להורים
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '25px' }}>
                    <button
                      onClick={handleUpdateSummary}
                      style={{
                        backgroundColor: '#a2b087',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 25px',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      disabled={loading}
                    >
                      {loading ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                          <div className="spinner" style={{
                            width: '18px',
                            height: '18px',
                            border: '3px solid rgba(255,255,255,0.3)',
                            borderRadius: '50%',
                            borderTop: '3px solid white',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                          שומר...
                        </span>
                      ) : (
                        <span>שמור שינויים</span>
                      )}
                    </button>
                    <button
                      onClick={cancelEditing}
                      style={{
                        backgroundColor: '#f5f5f5',
                        color: '#333',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 25px',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      בטל
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: !isMobile ? '40px 20px' : '30px 15px',
              backgroundColor: 'rgba(255,255,255,0.8)',
              borderRadius: '10px',
              margin: '20px 0'
            }}>
              <div style={{ 
                fontSize: !isMobile ? '3rem' : '2.5rem', 
                marginBottom: '10px', 
                color: '#ccc' 
              }}>📝</div>
              <p style={{ 
                fontSize: !isMobile ? '1.2rem' : '1.1rem', 
                color: '#666' 
              }}>לא נמצאו סיכומי שיעור עבור ילד זה.</p>
              <p style={{ 
                fontSize: !isMobile ? '1rem' : '0.9rem', 
                color: '#888', 
                marginTop: '10px' 
              }}>לחץ על "הוסף סיכום שיעור חדש" כדי להוסיף את הסיכום הראשון</p>
            </div>
          )}

          {/* כפתור הוספת סיכום חדש - הועבר לצד ההפוך */}
          <div style={{ 
            textAlign: 'right', // החזרה לצד הימני
            marginTop: '30px', 
            marginBottom: '20px',
            paddingRight: '40px' // רווח מהקצה הימני
          }}>
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                backgroundColor: showForm ? '#eec0c6' : '#d2e0cc',
                color: '#471f1f',
                border: 'none',
                borderRadius: '25px',
                padding: !isMobile ? '15px 40px' : '12px 30px',
                fontSize: !isMobile ? '1.2rem' : '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                direction: 'rtl' // חזרה לכיוון RTL
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = showForm ? '#dcc8ec' : '#d4ecee';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = showForm ? '#eec0c6' : '#d2e0cc';
              }}
            >
              {showForm ? 'בטל הוספה' : 'הוסף סיכום שיעור חדש'}
              <span style={{ fontSize: '1.4rem', fontWeight: 'bold' }}>
                {showForm ? '✕' : '+'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LessonSummariesPage;
